import type { CustomProduct } from "@apptus/esales-api";
import { Image } from "types";
import getNorceCdnImageUrl from "./getNorceCdnImageUrl";
import type { ElevateMarket } from "./utils";

function usableKey(key: unknown): string | undefined {
  if (typeof key !== "string") return undefined;
  const trimmed = key.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function usableKeys(keys: unknown): string[] {
  if (!Array.isArray(keys)) return [];

  const out: string[] = [];
  for (const key of keys) {
    const usable = usableKey(key);
    if (usable) out.push(usable);
  }
  return out;
}

function dedupe(keys: string[]): string[] {
  return [...new Set(keys)];
}

function pickKeys(primary: string[], fallback: string[]): string[] {
  return primary.length > 0 ? primary : fallback;
}

export default function getProductImages(
  product: CustomProduct,
  market: ElevateMarket,
  promoteProducts = false,
): Image[] {
  const json = product.typedCustom?.json;
  const main =
    usableKey(json?.v2_variantMainImageKey) ??
    usableKey(json?.v2_productMainImageKey);

  const additional = promoteProducts
    ? pickKeys(
        usableKeys(json?.v2_productAdditionalImageKey),
        usableKeys(json?.v2_variantAdditionalImageKey),
      )
    : pickKeys(
        usableKeys(json?.v2_variantAdditionalImageKey),
        usableKeys(json?.v2_productAdditionalImageKey),
      );

  const preferred = dedupe([...(main ? [main] : []), ...additional]);
  const keys = preferred.length > 0 ? preferred : usableKeys(json?.imagesKey);
  if (keys.length > 0) {
    return dedupe(keys).map((key) => ({
      src: getNorceCdnImageUrl(key, market),
      alt: product.title,
      id: product.key + "_image",
      type: "image",
    }));
  }

  const thumbnail = usableKey(product.imageInfo.thumbnail);
  return thumbnail
    ? [
        {
          src: getNorceCdnImageUrl(thumbnail, market),
          alt: product.title,
          id: product.key + "_image",
          type: "image",
        },
      ]
    : [];
}
